import { GenericDialog } from '@components/display/dialogs';
import { EventDetailMenu } from '@event/components/display/menus';
import { useEventDashboardContext } from '@event/contexts/EventContext';
import { EventFormikType } from '@event/types/event';
import Check from '@mui/icons-material/Check';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Button, CircularProgress, Typography } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers-pro';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface IEventDetailEditActionBarProps {
    onCancelClick: () => void;
    onDeleteClick: () => void;
    onSaveClick: (actualPublishedState?: boolean) => void;
    onSaveAndPublishClick: () => void;
    isLoading: boolean;
}

const EventDetailEditActionBar = ({
    onCancelClick,
    onDeleteClick,
    onSaveClick,
    onSaveAndPublishClick,
    isLoading,
}: IEventDetailEditActionBarProps) => {
    const { event, committedPublishedState } = useEventDashboardContext();
    const { values } = useFormikContext<EventFormikType>();
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);

    const handleSaveClick = async () => {
        const actualPublishedState = committedPublishedState !== undefined ? committedPublishedState : event?.published;

        if (actualPublishedState) {
            onSaveClick(actualPublishedState);
            return;
        }
        setSaveDialogOpen(true);
    };

    const handleSaveOnly = () => {
        setSaveDialogOpen(false);
        onSaveClick();
    };

    const handleSaveAndPublish = () => {
        setSaveDialogOpen(false);
        onSaveAndPublishClick();
    };

    const handleSaveCancel = () => {
        setSaveDialogOpen(false);
    };

    return (
        <>
            <Button
                component={Link}
                color="secondary-alt"
                startIcon={<ArrowBackRoundedIcon />}
                to="/app/dashboard/events"
                className="rounded-full black-border"
            >
                <Typography fontWeight={500} fontSize={15} letterSpacing={0.46}>
                    Return to overview
                </Typography>
            </Button>
            <div className="flex items-center gap-2">
                <Button
                    startIcon={<CloseIcon />}
                    onClick={onCancelClick}
                    color="secondary-alt"
                    className="w-[174px] rounded-full black-border"
                >
                    <Typography fontWeight={500} fontSize={15} letterSpacing={0.46}>
                        Cancel
                    </Typography>
                </Button>
                <Button
                    disabled={isLoading}
                    startIcon={!isLoading ? <Check /> : <CircularProgress size={20} color="inherit" />}
                    onClick={handleSaveClick}
                    className="w-[174px] rounded-full black-border"
                >
                    <Typography fontWeight={500} fontSize={15} letterSpacing={0.46}>
                        Save
                    </Typography>
                </Button>
                <EventDetailMenu onDeleteClick={onDeleteClick} hash={values.uuid as string} />
            </div>
            <GenericDialog
                open={saveDialogOpen}
                disableEscapeKeyDown={false}
                onClose={handleSaveCancel}
                titleSX={
                    <div className="flex flex-col items-center text-center">
                        <Typography
                            fontFamily="museo-sans"
                            fontSize={32}
                            fontWeight={500}
                            letterSpacing={-0.8}
                            lineHeight="110%"
                            className="mb-3"
                        >
                            Save and Publish?
                        </Typography>
                        <Typography fontSize={16}>
                            Would you like to publish the event immediately and make it live now?
                        </Typography>
                    </div>
                }
                secondaryBtnProps={{
                    title: 'Save Only',
                    onClick: handleSaveOnly,
                    startIcon: <Check />,
                    variant: 'contained',
                    color: 'primary-600',
                }}
                primaryBtnProps={{
                    title: 'Save and Publish',
                    onClick: handleSaveAndPublish,
                    color: 'warning-alt',
                    startIcon: <CalendarIcon />,
                }}
            />
        </>
    );
};

export { EventDetailEditActionBar };
